import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Security as SecurityIcon,
  Visibility,
  VisibilityOff,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import useUserStore from '../../stores/userStore';
import otpService from '../../services/otpService';

const OTPLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname
    ? `${location.state.from.pathname}${location.state.from.search || ''}`
    : '/';
  const { 
    sendOTP, 
    verifyOTP, 
    otpSent, 
    otpPhone, 
    isOTPExpired, 
    clearOTPState,
    loginFlag 
  } = useUserStore();

  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [formData, setFormData] = useState({
    phone: '',
    otp: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOTP, setShowOTP] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Redirect if already logged in
  useEffect(() => {
    if (loginFlag) {
      navigate(from, { replace: true });
    }
  }, [loginFlag, navigate, from]);

  // Handle countdown timer
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Check if OTP is expired
  useEffect(() => {
    if (otpSent && isOTPExpired()) {
      clearOTPState();
      setStep('phone');
      setError('OTP has expired. Please request a new one.');
    }
  }, [otpSent, isOTPExpired, clearOTPState]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      const formattedPhone = otpService.formatPhoneInput(value);
      setFormData({ ...formData, phone: formattedPhone });
    } else if (name === 'otp') {
      const formattedOTP = otpService.formatOTPInput(value);
      setFormData({ ...formData, otp: formattedOTP });
    }
    
    // Clear errors when user starts typing
    if (error) setError('');
  };

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await sendOTP(formData.phone);
      setSuccess(`OTP sent successfully! Code: ${result.otp_code} (Debug mode)`);
      setStep('otp');
      setCountdown(300); // 5 minutes countdown
    } catch (error) {
      setError(error.response?.data?.error || error.message || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await verifyOTP(formData.phone, formData.otp);
      setSuccess('Login successful! Redirecting...');
      setTimeout(() => navigate(from, { replace: true }), 1000);
    } catch (error) {
      setError(error.response?.data?.error || error.message || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    setError('');

    try {
      const result = await sendOTP(formData.phone);
      setSuccess(`OTP resent successfully! Code: ${result.otp_code} (Debug mode)`);
      setCountdown(300); // Reset countdown
    } catch (error) {
      setError(error.response?.data?.error || error.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPhone = () => {
    clearOTPState();
    setStep('phone');
    setFormData({ phone: '', otp: '' });
    setError('');
    setSuccess('');
    setCountdown(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box sx={{ mt: 8, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            {step === 'otp' && (
              <IconButton onClick={handleBackToPhone} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Typography component="h1" variant="h4" align="center" sx={{ flexGrow: 1 }}>
              {step === 'phone' ? 'Login with Phone' : 'Enter OTP'}
            </Typography>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Phone Number Step */}
          {step === 'phone' && (
            <Box component="form" onSubmit={handleSendOTP}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="phone"
                label="Phone Number"
                name="phone"
                type="tel"
                autoComplete="tel"
                autoFocus
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter 10-digit phone number"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon />
                    </InputAdornment>
                  ),
                }}
                helperText="Enter your registered phone number"
              />
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || formData.phone.length !== 10}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Send OTP'}
              </Button>
            </Box>
          )}

          {/* OTP Verification Step */}
          {step === 'otp' && (
            <Box component="form" onSubmit={handleVerifyOTP}>
              <Typography variant="body1" align="center" sx={{ mb: 3 }}>
                We've sent a 6-digit OTP to {formData.phone}
              </Typography>

              <TextField
                margin="normal"
                required
                fullWidth
                id="otp"
                label="OTP Code"
                name="otp"
                type={showOTP ? 'text' : 'password'}
                autoComplete="one-time-code"
                autoFocus
                value={formData.otp}
                onChange={handleChange}
                placeholder="Enter 6-digit OTP"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SecurityIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOTP(!showOTP)}
                        edge="end"
                      >
                        {showOTP ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Enter the 6-digit code sent to your phone"
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading || formData.otp.length !== 6}
                sx={{ mt: 3, mb: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Verify & Login'}
              </Button>

              {/* Resend OTP */}
              <Box sx={{ textAlign: 'center', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Didn't receive the OTP?{' '}
                  <Button
                    variant="text"
                    onClick={handleResendOTP}
                    disabled={countdown > 0 || loading}
                    size="small"
                  >
                    {countdown > 0 ? `Resend in ${formatTime(countdown)}` : 'Resend OTP'}
                  </Button>
                </Typography>
              </Box>
            </Box>
          )}

          <Divider sx={{ my: 3 }} />

          {/* Footer */}
          <Typography variant="body2" align="center" color="text.secondary">
            By continuing, you agree to our Terms of Service and Privacy Policy
          </Typography>
        </Paper>
      </Box>
    </Container>
  );
};

export default OTPLogin;
